import { IconType } from "react-icons";
import { FaPhoneAlt, FaPhoneSlash } from "react-icons/fa";
import { LuPhoneIncoming, LuPhoneOutgoing } from "react-icons/lu";
import { MdPhoneForwarded, MdVoicemail } from "react-icons/md";

import Badge, { type BadgeProps } from "@/components/ui/badge";
import { FilterOption, FilterTypes } from "@/components/ui/table/table-filter";

import { formatToHumanReadable } from "@/lib/format-data";

import { CallFetchParams, TagSummary } from "@/types/call";
import { CallDirection, CallStatus } from "@/types/enums/call";

export const getHeaders = () => [
  { label: "Date", sortField: "createdDate", className: "w-1/6" },
  { label: "From", sortField: "sender", className: "w-1/6" },
  { label: "To", sortField: "receiver", className: "w-1/6" },
  { label: "Status", sortField: "status", className: "w-1/6" },
  { label: "Summary", sortField: "summary", className: "w-1/3" },
];

export const filterTypes: {
  key: Exclude<
    keyof CallFetchParams,
    "page" | "perPage" | "sortBy" | "sortDirection"
  >;
  label: string;
  type: FilterTypes;
  options?: FilterOption[];
  prefix?: string;
}[] = [
  { key: "createdDate", label: "Date", type: "date" },
  { key: "userName", label: "Name", type: "text" },
  { key: "userId", label: "Patient ID", type: "id", prefix: "usr" },
  { key: "phoneNumber", label: "Phone Number", type: "phoneNumber" },
  { key: "id", label: "Call ID", type: "id", prefix: "ca" },
  {
    key: "direction",
    label: "Direction",
    type: "select",
    options: Object.values(CallDirection).map((direction) => ({
      value: direction,
      label: formatToHumanReadable(direction),
    })),
  },
  {
    key: "status",
    label: "Status",
    type: "multiselect",
    options: Object.values(CallStatus).map((status) => ({
      value: status,
      label: formatToHumanReadable(status),
    })),
  },
];

export const getFilterTypes = (tags: TagSummary[]) => {
  return [
    ...filterTypes,
    {
      key: "tags" as keyof CallFetchParams,
      label: "Tags",
      type: "multiselect" as FilterTypes,
      options: tags.map((tag: TagSummary) => ({
        value: tag.id,
        label: tag.name,
      })),
    },
    {
      key: "locationIds" as keyof CallFetchParams,
      label: "Location",
      type: "location" as FilterTypes,
    },
  ];
};

export const renderCallStatus = (status: CallStatus) => {
  switch (status) {
    case CallStatus.ONGOING:
      return (
        <Badge variant="success" className="gap-x-1.5">
          {/* Theme token rather than a hardcoded emerald, and the pulse stops
              under prefers-reduced-motion like every other live indicator. */}
          <span className="h-2 w-2 animate-pulse rounded-full bg-status-success-text motion-reduce:animate-none" />
          <span>Ongoing</span>
        </Badge>
      );
    case CallStatus.FINISHED:
      return (
        <Badge variant="default" icon={FaPhoneAlt}>
          Finished
        </Badge>
      );
    case CallStatus.VOICEMAIL:
      return (
        <Badge variant="warning" icon={MdVoicemail}>
          Voicemail
        </Badge>
      );
    case CallStatus.TRANSFERRED:
      return (
        <Badge variant="default" icon={MdPhoneForwarded}>
          Transferred
        </Badge>
      );
    case CallStatus.DISCONNECTED:
    case CallStatus.FAILED:
      return (
        <Badge variant="error" icon={FaPhoneSlash}>
          {formatToHumanReadable(status)}
        </Badge>
      );
    default:
      return <Badge variant="neutral">{status}</Badge>;
  }
};

/** Shared visual language for call status, so the list (dot) and the detail
 * pane (badge) stay in lockstep. `dotClass` is a solid background token used
 * for the small status dot in list rows; `badgeVariant` drives the full Badge. */
export interface StatusMeta {
  label: string;
  icon: IconType;
  badgeVariant: NonNullable<BadgeProps["variant"]>;
  dotClass: string;
  /** true for live/in-progress calls — drives the pulsing indicator. */
  live?: boolean;
}

export const getStatusMeta = (status: CallStatus): StatusMeta => {
  switch (status) {
    case CallStatus.ONGOING:
      return {
        label: "Ongoing",
        icon: FaPhoneAlt,
        badgeVariant: "success",
        dotClass: "bg-status-success-text",
        live: true,
      };
    case CallStatus.FINISHED:
      return {
        label: "Finished",
        icon: FaPhoneAlt,
        badgeVariant: "default",
        dotClass: "bg-status-success-text",
      };
    case CallStatus.VOICEMAIL:
      return {
        label: "Voicemail",
        icon: MdVoicemail,
        badgeVariant: "warning",
        dotClass: "bg-status-warning-text",
      };
    case CallStatus.TRANSFERRED:
      return {
        label: "Transferred",
        icon: MdPhoneForwarded,
        badgeVariant: "default",
        dotClass: "bg-status-info-text",
      };
    case CallStatus.DISCONNECTED:
    case CallStatus.FAILED:
      return {
        label: formatToHumanReadable(status),
        icon: FaPhoneSlash,
        badgeVariant: "error",
        dotClass: "bg-status-error-text",
      };
    default:
      return {
        label: formatToHumanReadable(status) || status,
        icon: FaPhoneAlt,
        badgeVariant: "neutral",
        dotClass: "bg-foreground-muted",
      };
  }
};

export interface DirectionMeta {
  label: string;
  icon: IconType;
}

export const getDirectionMeta = (direction: CallDirection): DirectionMeta =>
  direction === CallDirection.INBOUND
    ? { label: "Inbound", icon: LuPhoneIncoming }
    : { label: "Outbound", icon: LuPhoneOutgoing };
